import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CommonState,
  ApiNotification,
  ShowNotificationPayload,
  ApiCallStartPayload,
  ApiCallSuccessPayload,
  ApiCallErrorPayload,
} from './types';

const DEFAULT_DURATION = 5000;

const initialState: CommonState = {
  notifications: [],
  isLoading: false,
  lastApiCall: undefined,
};

const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

export const getNotificationMessage = (
  value: unknown,
  fallback = "",
): string => {
  if (typeof value === "string") return value.trim() || fallback;
  if (value instanceof Error) return value.message.trim() || fallback;
  if (Array.isArray(value)) {
    const firstMessage = value
      .map((entry) => getNotificationMessage(entry))
      .find(Boolean);
    return firstMessage || fallback;
  }
  if (!value || typeof value !== "object") return fallback;

  const record = value as Record<string, unknown>;
  const directMessage =
    getNotificationMessage(record.message) ||
    getNotificationMessage(record.error) ||
    getNotificationMessage(record.detail) ||
    getNotificationMessage(record.title);
  if (directMessage) return directMessage;

  const nestedMessage =
    getNotificationMessage(record.data) ||
    getNotificationMessage(record.errors) ||
    getNotificationMessage(record.errorMessages);

  return nestedMessage || fallback;
};

const createNotification = (
  type: ApiNotification['type'],
  title: string,
  message?: unknown,
  duration = DEFAULT_DURATION,
  status?: string,
  actionLabel?: string,
  actionUrl?: string,
): ApiNotification => ({
  id: generateId(),
  type,
  title,
  message: getNotificationMessage(message) || undefined,
  duration,
  status,
  actionLabel,
  actionUrl,
  timestamp: Date.now(),
  isVisible: true,
});

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    showNotification: (state, action: PayloadAction<ShowNotificationPayload>) => {
      const { type, title, message, duration, status, actionLabel, actionUrl } = action.payload;
      state.notifications.push(createNotification(type, title, message, duration, status, actionLabel, actionUrl));
    },

    hideNotification: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) notification.isVisible = false;
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    clearAllNotifications: (state) => {
      state.notifications = [];
    },

    apiCallStart: (state, action: PayloadAction<ApiCallStartPayload>) => {
      state.isLoading = true;
      state.lastApiCall = { endpoint: action.payload.endpoint, timestamp: Date.now(), status: 'pending' };
    },

    apiCallSuccess: (state, action: PayloadAction<ApiCallSuccessPayload>) => {
      const { endpoint, message, showNotification = true } = action.payload;
      state.isLoading = false;
      if (state.lastApiCall?.endpoint === endpoint) state.lastApiCall.status = 'success';
      if (showNotification && message) {
        state.notifications.push(createNotification('success', 'Success', message));
      }
    },

    apiCallError: (state, action: PayloadAction<ApiCallErrorPayload>) => {
      const { endpoint, error, showNotification = true } = action.payload;
      state.isLoading = false;
      if (state.lastApiCall?.endpoint === endpoint) state.lastApiCall.status = 'error';
      if (showNotification) {
        state.notifications.push(createNotification('error', 'Error', error, DEFAULT_DURATION));
      }
    },

    showSuccessNotification: (state, action: PayloadAction<{ title: string; message?: unknown; duration?: number; status?: string; actionLabel?: string; actionUrl?: string }>) => {
      const { title, message, duration, status, actionLabel, actionUrl } = action.payload;
      state.notifications.push(createNotification('success', title, message, duration, status, actionLabel, actionUrl));
    },

    showErrorNotification: (state, action: PayloadAction<{ title: string; message?: unknown; duration?: number; status?: string; actionLabel?: string; actionUrl?: string }>) => {
      const { title, message, duration, status, actionLabel, actionUrl } = action.payload;
      state.notifications.push(createNotification('error', title, message, duration, status, actionLabel, actionUrl));
    },

    showWarningNotification: (state, action: PayloadAction<{ title: string; message?: unknown; duration?: number; status?: string; actionLabel?: string; actionUrl?: string }>) => {
      const { title, message, duration, status, actionLabel, actionUrl } = action.payload;
      state.notifications.push(createNotification('warning', title, message, duration, status, actionLabel, actionUrl));
    },

    showInfoNotification: (state, action: PayloadAction<{ title: string; message?: unknown; duration?: number; status?: string; actionLabel?: string; actionUrl?: string }>) => {
      const { title, message, duration, status, actionLabel, actionUrl } = action.payload;
      state.notifications.push(createNotification('info', title, message, duration, status, actionLabel, actionUrl));
    },

    resetLoadingState: (state) => {
      state.isLoading = false;
    },

    clearOldNotifications: (state) => {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      state.notifications = state.notifications.filter(n => n.timestamp > fiveMinutesAgo || n.isVisible);
    },
  },
});

// Export actions
export const {
  showNotification,
  hideNotification,
  removeNotification,
  clearAllNotifications,
  apiCallStart,
  apiCallSuccess,
  apiCallError,
  showSuccessNotification,
  showErrorNotification,
  showWarningNotification,
  showInfoNotification,
  resetLoadingState,
  clearOldNotifications,
} = commonSlice.actions;

// Export reducer
export default commonSlice.reducer;
